<template>
  <PageHeader
    :title="nodePageData?.label"
    :back-link="resolveBackUrl"
    show-back
    border-color="border-[#595959]"
    class="text-white"
  />

  <Container
    class="mx-auto mb-30 md:w-[67.48%] lg:w-[76.5%] xl:mb-42.5 xl:w-[67.85%]"
  >
    <NodePageItem
      v-for="node in enrichedChildren"
      :key="node.id"
      :node="node"
      :page-type="node.type"
    />
  </Container>
</template>

<script setup lang="ts">
import type { MenuItem } from '~/types'

const props = defineProps<{ pageData?: Record<string, any> }>()

const { locale } = useI18n()
const route = useRoute()
const setI18nParams = useSetI18nParams()
const theme = useTheme()
const breadcrumbsData = useBreadcrumbs()

onMounted(() => {
  theme.value = 'dark'
})

onBeforeUnmount(() => {
  theme.value = 'light'
})

// If pageData is passed as prop (from [...slug].vue), skip the duplicate fetch
let fetchedData: { value: Record<string, any> } | null = null
if (!props.pageData) {
  const { data } = await usePersistedFetch('GetPageBySlug', {
    slug: route.fullPath,
  })
  fetchedData = data
}

const { menuItems } = await useMenuItems()

const nodePageData = computed(
  () => props.pageData ?? fetchedData?.value?.getPageBySlug,
)

const enrichedChildren = computed(() => {
  const children = nodePageData.value?.children || []
  if (!menuItems.value?.length) return children

  const findMenuItem = (items: MenuItem[], id: string): MenuItem | null => {
    for (const item of items) {
      if (String(item.id) === String(id)) return item
      if (item.children?.length) {
        const found = findMenuItem(item.children, id)
        if (found) return found
      }
    }
    return null
  }

  return children.map((child: any) => {
    const matched = findMenuItem(menuItems.value, child.id)
    if (matched) {
      return {
        ...child,
        children: matched.children || [],
      }
    }
    return child
  })
})

useDynamicSeo(nodePageData, {
  bodyAttrs: {
    class: 'bg-black text-white',
  },
})

onMounted(() => {
  breadcrumbsData.value = nodePageData.value.breadcrumbs[locale.value]
})

watch(
  nodePageData,
  (val) => {
    if (val) {
      setI18nParams({
        cs: { slug: useGetSlugSegments(val, 'cs') },
        en: { slug: useGetSlugSegments(val, 'en') },
      })
    }
  },
  { immediate: true },
)

const resolveBackUrl = computed(() => {
  const url = nodePageData.value?.menu_url
  if (!url) return '/'

  const cleanUrl = url.replace(/\/$/, '')
  const parentUrl = cleanUrl.substring(0, cleanUrl.lastIndexOf('/'))
  return parentUrl || '/'
})
</script>
